/**
 * readHealth — "is this device still RECEIVING changes from the server?"
 *
 * WHY (Walter 2026-08-21). With Ghostery on, his console showed
 * `net::ERR_BLOCKED_BY_CLIENT` on Firestore's `Listen/channel` and
 * `Write/channel`, yet his saves kept working — the SDK falls back to plain
 * REST commits for writes. So the WRITE half mostly survives a blocker and
 * the READ half does not: no realtime updates arrive at all, another device's
 * edits never show up, and the header cheerfully says "synced" throughout.
 *
 * That is very likely the real engine behind his conflicts. Two devices that
 * cannot see each other's edits both work from stale content, and the collision
 * happens on the next write.
 *
 * The existing monitor's `connectionBlocked` flag CANNOT catch this: it is
 * gated on `!everSynced`, so once any server data has flowed once — which it
 * does at startup — it can never fire again. `isStale` is the usable signal:
 * it mirrors `snapshot.metadata.fromCache`, which stays true for exactly as
 * long as the listener is being served from cache instead of the server.
 *
 * Conservative on purpose. `fromCache` flickers true on load and during any
 * brief reconnect, and a false "you are blocked" banner is worse than none —
 * a previous heuristic was removed for exactly that (see ExtensionBlockHint's
 * 2026-05-31 note). So we require it to persist, while online and visible.
 */

import { getSyncState, subscribeSync } from '../hooks/useFirestoreSyncMonitor';

// How long the listener must be continuously cache-served before we conclude
// reads are dead rather than slow. A blocked channel never recovers; a bad
// network usually does inside a couple of minutes.
export const READ_DEAD_MS = 3 * 60 * 1000;

let staleSince = null;   // when the current uninterrupted stale run began
let readsDead = false;
const listeners = new Set();
let timer = null;

function emit() {
  for (const fn of listeners) {
    try { fn(readsDead); } catch { /* never let a listener break sync */ }
  }
}

function evaluate() {
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
  const { isStale } = getSyncState();

  if (!isStale || !online || !visible) {
    // Fresh server data, offline (a different and honest problem), or the tab
    // is in the background where listeners are expected to idle.
    staleSince = null;
    if (readsDead) { readsDead = false; emit(); }
    return;
  }
  if (staleSince == null) staleSince = Date.now();
  const dead = Date.now() - staleSince >= READ_DEAD_MS;
  if (dead !== readsDead) { readsDead = dead; emit(); }
}

/** Begin watching. Idempotent; returns a stop function. */
export function startReadHealth() {
  if (timer) return stopReadHealth;
  const unsub = subscribeSync(evaluate);
  timer = setInterval(evaluate, 10000);
  evaluate();
  stopReadHealth.unsub = unsub;
  return stopReadHealth;
}

export function stopReadHealth() {
  if (timer) { clearInterval(timer); timer = null; }
  if (stopReadHealth.unsub) { stopReadHealth.unsub(); stopReadHealth.unsub = null; }
  staleSince = null;
  readsDead = false;
}

export function areReadsDead() { return readsDead; }

export function subscribeReadHealth(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Test seam. */
export function __setReadsDeadForTest(v) { readsDead = v; emit(); }
